'use client';

import { type ReactNode, useEffect, useState } from 'react';

import { useOnCheckoutSuccess } from '@ps/features/checkout/notification/useOnCheckoutSuccess';
import { proxyFetch } from '@ps/functions/utils/proxyFetch';
import { kPseudoCreatorIdForPreview } from '@ps/models/constants';
import { useCurrentUserId } from '@ps/ui/contexts/auth';

import {
  type CurrentUserSubscriptionState,
  deriveSubscriptionState,
} from '../../../subscription/functions/currentUserSubscriptionState';
import { synthesizeExtendedSubscriptionLevel } from '../../../subscription/functions/synthesizeExtendedSubscriptionLevel';
import {
  BioUserAccessContext,
  type BioUserAccessContextValue,
} from './context';

interface Props {
  /** The bio's creator id (`profile.id`) — the other side of the relationship. */
  creatorId: string;
  /**
   * Subscribe to the follow status live via Firestore (`onSnapshot`). OFF by
   * default: the follow status is resolved once through the server
   * (`/api/bio/following-status`, firebase-admin) and thereafter driven
   * imperatively via `updateFollowingStatus` / `refreshFollowingStatus` — so
   * `firebase/firestore` never enters the visitor bundle. Turn it on only where a
   * live follow/unfollow must reflect without an explicit refresh.
   */
  realtimeFollowingStatusEnabled?: boolean;
  children: ReactNode;
}

const EMPTY_SUBSCRIPTION: CurrentUserSubscriptionState = {
  accessLevel: undefined,
  activeSubscriptionLevel: undefined,
  canceled: undefined,
  queuedSubscription: undefined,
};

interface FollowingStatusResponse {
  following: boolean;
}

/**
 * Resolves the signed-in viewer's subscription + follow standing with the bio's
 * creator and shares it via {@link BioUserAccessContext}. Anonymous viewers
 * resolve to the stranger state without a round-trip.
 *
 * Both lookups keep heavy firebase off the public-bio first-load bundle: the
 * subscription check is dynamic-imported (auth + AWS creator API — no
 * firestore), and the follow status is, by default, a one-shot SERVER check
 * (`/api/bio/following-status`, firebase-admin) rather than a client
 * `onSnapshot`, so `firebase/firestore` never loads at all.
 * `realtimeFollowingStatusEnabled` opts back into the live listener (which does
 * load `firebase/firestore`) where a follow/unfollow must reflect immediately;
 * otherwise consumers drive updates via `updateFollowingStatus` /
 * `refreshFollowingStatus`.
 */
export function BioUserAccessProvider({
  creatorId,
  realtimeFollowingStatusEnabled = false,
  children,
}: Props) {
  const currentUserId = useCurrentUserId();

  // The AI-profile-builder preview mounts the bio against a pseudo creator id
  // (`__PSEUDO_CREATOR_ID_FOR_PREVIEW__`) that has no real relationship data —
  // and being a RESERVED id, any subscription/following query against it throws.
  // Treat it like "no relationship to resolve" (same as the self-view case).
  const isPreviewCreator = creatorId === kPseudoCreatorIdForPreview;

  const [subscription, setSubscription] =
    useState<CurrentUserSubscriptionState>(EMPTY_SUBSCRIPTION);
  const [isFollowing, setIsFollowing] = useState<boolean | undefined>(
    undefined,
  );
  const [subscriptionResolved, setSubscriptionResolved] = useState(false);
  const [followResolved, setFollowResolved] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [followNonce, setFollowNonce] = useState(0);

  // A community-membership subscription is the one checkout that changes the
  // viewer's standing with a creator. Re-resolve when one completes so gated
  // surfaces (clip paywall, course banner) lift on return WITHOUT a reload and
  // without each consumer wiring its own refresh. Matched on type only — this
  // provider is per-creator, so a stray re-fetch for another creator's sub is a
  // harmless no-op (the success URL identifies the creator by url_code, which
  // this provider doesn't carry).
  useOnCheckoutSuccess(event => {
    if (event.type === 'community_membership_subscription') {
      setRefreshNonce(nonce => nonce + 1);
    }
  });

  // ── Subscription standing — one-shot (auth + AWS creator API, no firestore) ──
  useEffect(() => {
    if (currentUserId === undefined) {
      setSubscriptionResolved(false);
      return;
    }
    // Logged out, the creator viewing their own bio, or the preview pseudo
    // creator — nothing to fetch.
    if (
      currentUserId === null ||
      currentUserId === creatorId ||
      isPreviewCreator
    ) {
      setSubscription(EMPTY_SUBSCRIPTION);
      setSubscriptionResolved(true);
      return;
    }

    const uid = currentUserId;
    const controller = new AbortController();
    let disposed = false;
    setSubscriptionResolved(false);

    void (async () => {
      const { checkUserSubscriptionStatus } =
        await import('../../../subscription/functions/checkUserSubscriptionStatus');
      if (disposed) return;
      const data = await checkUserSubscriptionStatus({
        userId: uid,
        creatorId,
        signal: controller.signal,
      }).catch(error => {
        // An abort is the expected outcome when the effect re-runs (e.g. an auth
        // change) or unmounts — not a failure, so don't surface it.
        if (disposed || controller.signal.aborted) return null;
        console.error(
          'Bio access: resolving subscription standing failed:',
          error,
        );
        return null;
      });
      if (disposed) return;
      setSubscription(deriveSubscriptionState(data));
      setSubscriptionResolved(true);
    })();

    return () => {
      disposed = true;
      controller.abort();
    };
  }, [currentUserId, creatorId, refreshNonce]);

  // ── Follow standing — opt-in live Firestore listener, else a server check ──
  useEffect(() => {
    if (currentUserId === undefined) {
      setFollowResolved(false);
      return;
    }
    if (
      currentUserId === null ||
      currentUserId === creatorId ||
      isPreviewCreator
    ) {
      // Anonymous / preview → not following; the creator on their own bio → no
      // relationship.
      setIsFollowing(currentUserId === creatorId ? undefined : false);
      setFollowResolved(true);
      return;
    }

    const uid = currentUserId;
    let disposed = false;
    setFollowResolved(false);

    // Realtime (opt-in): a live listener so a follow/unfollow reflects at once.
    // Dynamic-imported so `firebase/firestore` loads ONLY on this path.
    if (realtimeFollowingStatusEnabled) {
      let unsubscribe: (() => void) | undefined;
      void (async () => {
        const { doc, firestore, onSnapshot } =
          await import('@ps/functions/firebase/firestore');
        if (disposed) return;
        const followRef = doc(
          firestore(),
          'profiles',
          uid,
          'followings',
          creatorId,
        );
        unsubscribe = onSnapshot(
          followRef,
          snap => {
            if (disposed) return;
            setIsFollowing(snap.exists());
            setFollowResolved(true);
          },
          error => {
            console.error(
              'Bio access: follow status subscription failed:',
              error,
            );
            if (disposed) return;
            setIsFollowing(false);
            setFollowResolved(true);
          },
        );
        // Cleaned up while the import was in flight — tear down immediately.
        if (disposed) unsubscribe();
      })();
      return () => {
        disposed = true;
        unsubscribe?.();
      };
    }

    // Default: one-shot server check via the proxy route — keeps
    // `firebase/firestore` out of the visitor bundle entirely.
    const controller = new AbortController();
    void (async () => {
      try {
        const params = new URLSearchParams({ creatorId });
        const { following } = await proxyFetch<FollowingStatusResponse>(
          `/api/bio/following-status?${params.toString()}`,
          { signal: controller.signal },
        );
        if (disposed) return;
        setIsFollowing(following);
        setFollowResolved(true);
      } catch (error) {
        if (disposed || controller.signal.aborted) return;
        console.error('Bio access: follow status check failed:', error);
        setIsFollowing(false);
        setFollowResolved(true);
      }
    })();
    return () => {
      disposed = true;
      controller.abort();
    };
  }, [
    currentUserId,
    creatorId,
    realtimeFollowingStatusEnabled,
    refreshNonce,
    followNonce,
  ]);

  const value: BioUserAccessContextValue = {
    currentUserId,
    creatorId,
    activeSubscriptionLevel: subscription.activeSubscriptionLevel,
    isFollowing,
    extendedSubscriptionLevel: synthesizeExtendedSubscriptionLevel(
      subscription.accessLevel,
      isFollowing === true,
    ),
    loading: !(subscriptionResolved && followResolved),
    refresh: () => setRefreshNonce(nonce => nonce + 1),
    updateFollowingStatus: (following: boolean) => {
      setIsFollowing(following);
      setFollowResolved(true);
    },
    refreshFollowingStatus: () => setFollowNonce(nonce => nonce + 1),
  };

  return (
    <BioUserAccessContext.Provider value={value}>
      {children}
    </BioUserAccessContext.Provider>
  );
}
